import { useEffect } from 'react';
import { refreshTokenApi } from '../api/auth';
import { useAuthStore } from '../store/authStore';
import { coordinatedTokenRefresh } from '../utils/authSessionCoordinator';
import { getProactiveRefreshDelayMs } from '../utils/accessTokenLifetime';
import { resolveCsrfToken } from '../utils/csrf';
import { isSilentRefreshBlocked } from '../utils/invalidateAuthSession';

/**
 * Refreshes the access token before it expires to reduce 401 storms and refresh races.
 */
export function useProactiveTokenRefresh(): void {
  const token = useAuthStore((state) => state.token);
  const csrfToken = useAuthStore((state) => state.csrfToken);

  useEffect(() => {
    if (!token || isSilentRefreshBlocked() || useAuthStore.getState().user?.isImpersonating) {
      return;
    }

    const schedule = (): (() => void) => {
      const delayMs = getProactiveRefreshDelayMs();
      if (delayMs === null) {
        return () => undefined;
      }

      const timeoutId = window.setTimeout(() => {
        void runProactiveRefresh();
      }, delayMs);

      return () => {
        window.clearTimeout(timeoutId);
      };
    };

    const cancel = schedule();

    return () => {
      cancel();
    };
  }, [token, csrfToken]);
}

async function runProactiveRefresh(): Promise<void> {
  const { token: accessToken, csrfToken } = useAuthStore.getState();
  const csrf = resolveCsrfToken(csrfToken);

  if (!csrf) {
    return;
  }

  await coordinatedTokenRefresh(() =>
    refreshTokenApi({
      accessToken: accessToken ?? null,
      csrfToken: csrf,
    })
  );
}
